import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { Loader2, BarChart3, EyeOff } from 'lucide-react';
import { projectId } from '../../utils/supabase/info';
import type { CommunicationMetrics as CommunicationMetricsType, MetricsSide } from './types';

interface CommunicationMetricsProps {
  engagementId: string;
  engagementName: string;
  accessToken: string;
  /** EL can disable metrics in client-facing status reports */
  showToggle?: boolean;
  onVisibilityChange?: (visible: boolean) => void;
  visible?: boolean;
}

interface MetricRow {
  label: string;
  clientValue: string | number;
  firmValue: string | number;
  highlight?: 'client' | 'firm' | null;
}

export function CommunicationMetrics({
  engagementId,
  engagementName,
  accessToken,
  showToggle = false,
  onVisibilityChange,
  visible = true,
}: CommunicationMetricsProps) {
  const [metrics, setMetrics] = useState<CommunicationMetricsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(visible);

  const baseUrl = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd`;

  useEffect(() => {
    loadMetrics();
  }, [engagementId]);

  const loadMetrics = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${baseUrl}/engagement-messaging/${engagementId}/metrics?period=current_week`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setMetrics(data.metrics);
      }
    } catch (error) {
      console.error('Error loading communication metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (checked: boolean) => {
    setIsVisible(checked);
    onVisibilityChange?.(checked);
  };

  if (!isVisible && showToggle) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <EyeOff className="h-4 w-4 text-muted-foreground" />
              Communication Health (Hidden)
            </CardTitle>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-muted-foreground">Show in report</Label>
              <Switch checked={isVisible} onCheckedChange={handleToggle} />
            </div>
          </div>
        </CardHeader>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!metrics) {
    return null;
  }

  const rows: MetricRow[] = [
    {
      label: 'Avg Response Time',
      clientValue: metrics.clientTeam.avgResponseTimeHours > 0
        ? `${metrics.clientTeam.avgResponseTimeHours}h`
        : '--',
      firmValue: metrics.consultingFirm.avgResponseTimeHours > 0
        ? `${metrics.consultingFirm.avgResponseTimeHours}h`
        : '--',
      highlight: null,
    },
    {
      label: 'Overdue Responses',
      clientValue: metrics.clientTeam.overdueResponses,
      firmValue: metrics.consultingFirm.overdueResponses,
      highlight:
        metrics.clientTeam.overdueResponses > metrics.consultingFirm.overdueResponses
          ? 'client'
          : metrics.consultingFirm.overdueResponses > metrics.clientTeam.overdueResponses
          ? 'firm'
          : null,
    },
    {
      label: 'Escalations Triggered',
      clientValue: metrics.clientTeam.escalationsTriggered,
      firmValue: metrics.consultingFirm.escalationsTriggered,
      highlight: null,
    },
    {
      label: 'SLA Adherence %',
      clientValue: `${metrics.clientTeam.slaAdherencePercent}%`,
      firmValue: `${metrics.consultingFirm.slaAdherencePercent}%`,
      highlight: null,
    },
  ];

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
            Communication Health
          </CardTitle>
          <div className="flex items-center gap-2">
            {showToggle && (
              <>
                <Label className="text-xs text-muted-foreground">Show in report</Label>
                <Switch checked={isVisible} onCheckedChange={handleToggle} />
              </>
            )}
            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
              This Week
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs font-medium w-[40%]">Metric</TableHead>
              <TableHead className="text-xs font-medium text-center">Client Team</TableHead>
              <TableHead className="text-xs font-medium text-center">Consulting Firm</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.label}>
                <TableCell className="text-xs font-medium text-muted-foreground py-2.5">
                  {row.label}
                </TableCell>
                <TableCell
                  className={`text-xs text-center py-2.5 font-medium ${
                    row.highlight === 'client' ? 'text-amber-600' : 'text-foreground'
                  }`}
                >
                  {row.clientValue}
                </TableCell>
                <TableCell
                  className={`text-xs text-center py-2.5 font-medium ${
                    row.highlight === 'firm' ? 'text-amber-600' : 'text-foreground'
                  }`}
                >
                  {row.firmValue}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <p className="text-[10px] text-muted-foreground mt-3 text-right">
          Computed {new Date(metrics.computedAt).toLocaleString(undefined, {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </p>
      </CardContent>
    </Card>
  );
}
